import { useState, useCallback } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize from 'rehype-sanitize'
import { ImageLightbox } from './ImageLightbox'
import type { DisplayMessage } from '@/hooks/useAndrzej'

interface Props {
  message: DisplayMessage
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }, [text])

  return (
    <button
      onClick={handleCopy}
      className="absolute top-1 right-1 flex items-center justify-center w-6 h-6 rounded opacity-0 group-hover:opacity-100 transition-opacity bg-paper-dark/80 border border-desk text-frame hover:text-botanical cursor-pointer"
      title={copied ? 'Skopiowano!' : 'Kopiuj'}
    >
      {copied ? (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      ) : (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="9" y="9" width="13" height="13" rx="2"/>
          <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
        </svg>
      )}
    </button>
  )
}

function ToolCallBadge({ icon, label, status }: { icon: string; label: string; status?: 'loading' | 'completed' }) {
  const isLoading = status === 'loading'
  return (
    <div
      className="flex items-center gap-[8px] rounded-lg py-[6px] px-[10px] pl-[14px] animate-[message-in_0.2s_ease-out] border border-desk border-l-[3px] border-l-botanical"
      style={{
        background: 'color-mix(in srgb, var(--paper) 70%, var(--desk))',
      }}
    >
      <span className="shrink-0 text-[0.85rem] leading-none text-botanical">{icon}</span>
      <span className={`flex-1 text-[0.75rem] ${isLoading ? 'text-ink-light' : 'font-medium text-botanical'}`}>
        {label}
      </span>
      {isLoading ? (
        <span className="inline-block h-[14px] w-[14px] rounded-full animate-spin border-2 border-desk border-t-botanical" />
      ) : (
        <span className="text-[0.85rem] leading-none text-botanical">&#10003;</span>
      )}
    </div>
  )
}

function MessageAttachments({ attachments }: { attachments: { url: string; filename: string }[] }) {
  const [lightboxSrc, setLightboxSrc] = useState<{ url: string; alt: string } | null>(null)

  if (!attachments?.length) return null

  return (
    <>
      <div className="flex flex-wrap gap-1 mb-xs">
        {attachments.map((att, i) => (
          <img
            key={i}
            src={att.url}
            alt={att.filename}
            className="max-h-40 rounded object-cover cursor-pointer hover:brightness-90 transition-[filter]"
            onClick={() => setLightboxSrc({ url: att.url, alt: att.filename })}
          />
        ))}
      </div>
      {lightboxSrc && (
        <ImageLightbox
          src={lightboxSrc.url}
          alt={lightboxSrc.alt}
          onClose={() => setLightboxSrc(null)}
        />
      )}
    </>
  )
}

export function MessageBubble({ message }: Props) {
  // Synthetic runtime tool indicator
  if (message._synthetic) {
    return <ToolCallBadge icon={message._toolIcon || '🔍'} label={message._toolLabel || ''} status={message._toolStatus} />
  }

  // Skip raw tool/system messages
  if (message.role === 'tool' || message.role === 'system') return null

  const isUser = message.role === 'user'
  const hasToolCalls = message.tool_calls && message.tool_calls.length > 0
  const hasContent = message.content && message.content.trim().length > 0

  // Tool-call-only assistant message (persisted from history)
  if (!isUser && hasToolCalls && !hasContent) {
    return (
      <div className="space-y-[4px] mb-sm">
        {message.tool_calls!.map((tc, i) => (
          <ToolCallBadge key={i} icon={tc.icon} label={tc.label} status="completed" />
        ))}
      </div>
    )
  }

  // User message
  if (isUser) {
    return (
      <div className="flex justify-end mb-sm animate-[message-in_0.2s_ease-out]">
        <div className="text-[0.8rem] leading-[1.5] px-sm py-xs max-w-[90%] bg-paper-dark border border-desk">
          <MessageAttachments attachments={message.attachments} />
          <p className="m-0 whitespace-pre-wrap">{message.content}</p>
        </div>
      </div>
    )
  }

  // Assistant message with content
  return (
    <div className="group flex gap-xs mb-sm items-start animate-[message-in_0.2s_ease-out]">
      <img src="/andrzej/andrzej-default.svg" alt=""
        className="h-[1.6rem] w-[1.6rem] rounded-full object-cover shrink-0 mt-[2px]" />
      <div className="relative text-[0.8rem] leading-[1.5] px-sm py-xs max-w-[90%] bg-paper border border-frame border-l-2 border-l-botanical">
        <CopyButton text={message.content || ''} />
        <div className="[&_p]:m-0 [&_p+p]:mt-[0.3rem] [&_a]:underline [&_ul]:ml-md [&_ol]:ml-md [&_li]:text-[0.8rem]"
          style={{ '--tw-prose-links': 'var(--botanical)' } as React.CSSProperties}>
          <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>{message.content || ''}</Markdown>
        </div>
      </div>
    </div>
  )
}
